import { Button } from "@mui/material";
import { formatLabel } from "helpers/functions/formatLabel";
import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { useEffect, useState } from "react";
import {
  Card,
  Col,
  Container,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { fetchTrashQuotationById } from "slices/Quotation/thunk";

interface CustomerDetails {
  area: string;
  city: string;
  email: string;
  gstin: string;
  state: string;
  status: boolean;
  address: string;
  country: string;
  pincode: string;
  mobile_no: string;
  created_at: string;
  created_by: string;
  is_deleted: boolean;
  pan_number: string;
  updated_at: string;
  updated_by: string | null;
  customer_id: string;
  customer_name: string;
  created_by_name: string;
  updated_by_name: string | null;
  contact_person_name: string;
  contact_person_mobile_no: string;
}

interface ItemAttribute {
  attribute_id: string;
  attribute_name: string;
  manage_value_id: string;
  item_attribute_id: string;
  manage_value_name: string;
}

interface ItemDetail {
  gst: string;
  tags: string | null;
  image: string | null;
  item_id: string;
  remarks: string;
  category: string;
  discount: number;
  item_sku: string;
  quantity: number;
  tag_name: string | null;
  tax_rate: number;
  item_name: string;
  attributes: ItemAttribute[];
  tax_amount: number;
  item_amount: number;
  category_name: string;
  discount_type: string;
  selling_price: string;
  taxable_value: number;
  brand_name?: string;
}

interface Image {
  img_id: string;
  image: string;
}

interface ReferenceImage {
  ref_img_id: string;
  image: string;
}

interface AssemblyCharges {
  price?: string;
  total?: number;
  discount?: string;
  discount_type?: string;
}

interface QuotationData {
  quotation_id: string;
  quotation_auto_id: string;
  quotation_date: string;
  validity: string;
  customer_details: CustomerDetails;
  quotation_creation_type: string;
  item_details: Record<string, ItemDetail[]>;
  assembly_charges: AssemblyCharges;
  other_charges: Array<{
    name: string;
    price: number;
    total: number;
    remark: string;
    discount: string;
    discount_type: string;
  }>;
  notes: string;
  images: Image[];
  reference_images: ReferenceImage[];
  subtotal: string;
  total_item_discount: string;
  net_taxable_value: string;
  total_gst_amount: string;
  quotation_total_amount: string;
  status: boolean;
  is_deleted: boolean;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_by: string;
  updated_by_name: string;
  updated_at: string;
  user_created_by_name: string;
}

// Define RootState interface for type-safe Redux state
interface RootStateQuotation {
  QuotationReducer: {
    selectedQuotation: QuotationData | null;
    loading: boolean;
  };
}

interface ModalImage {
  img_id: string;
  image: string;
  url: string;
}

const ViewTrashQuotationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const handleBack = useHandleBack("/quotation");
  const dispatch = useDispatch<any>();

  const [modalImage, setModalImage] = useState<ModalImage | null>(null);

  const { selectedQuotation, loading } = useSelector(
    (state: RootStateQuotation) => state.QuotationReducer
  );

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const isValidityExpired = (validityDate: string): boolean => {
    if (!validityDate) return false;
    const validity = new Date(validityDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return validity < today;
  };

  const itemsList = selectedQuotation?.item_details
    ? Object.entries(selectedQuotation.item_details).reduce<
        Array<{ category: string; items: ItemDetail[] }>
      >((acc, [category, items]) => {
        if (items.length > 0) {
          acc.push({
            category: category.replace("_", " ").toUpperCase(),
            items,
          });
        }
        return acc;
      }, [])
    : [];

  const calculateTotal = (items: ItemDetail[]): string => {
    return items
      .reduce(
        (total, item) => total + parseFloat(item.selling_price) * item.quantity,
        0
      )
      .toLocaleString("en-IN");
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchTrashQuotationById(id));
    }
  }, [id, dispatch]);

  return (
    <Container fluid className="page-content">
      {/* Header Section */}
      <Card className="p-1 mb-4 shadow-sm rounded-3">
        <Card.Header className="mb-3">
          <Row className="align-items-center g-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
              {/* Header */}
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ fontSize: "24px", cursor: "pointer" }}
                  onClick={handleBack}
                >
                  ←
                </span>
                <h5 className="mb-0 fw-bold">View Quotation</h5>
              </div>

              {/* Right: Breadcrumb */}
              <div className="text-muted">
                <span
                  onClick={handleBack}
                  style={{ color: "#6c757d", cursor: "pointer" }}
                >
                  Quotation
                </span>{" "}
                &gt;{" "}
                <span style={{ color: "#6c757d", cursor: "pointer" }}>
                  View Quotation
                </span>
              </div>
            </div>
          </Row>
        </Card.Header>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : !selectedQuotation ? (
          <div className="d-flex flex-column align-items-center gap-3 my-4">
            <h5 className="mb-0 fw-bold text-center">
              No quotation details found
            </h5>
            <Button
              variant="outlined"
              className="btn btn-secondary"
              onClick={handleBack}
            >
              Back
            </Button>
          </div>
        ) : (
          <Card.Body>
            <Row className="gy-2 px-2 g-4">
              <Col xs={12} md={6} xl={4}>
                <strong>Quotation No. :</strong>{" "}
                {selectedQuotation.quotation_auto_id}
              </Col>
              <Col xs={12} md={6} xl={6}>
                <strong>Initiated By :</strong>{" "}
                {selectedQuotation.user_created_by_name}
              </Col>
              <Col xs={12} md={6} xl={4}>
                <strong>Quotation Date :</strong>{" "}
                {formatDate(selectedQuotation.quotation_date)}
              </Col>
              <Col xs={12} md={6} xl={4}>
                <strong>Valid Until :</strong>{" "}
                <span
                  style={{
                    color: isValidityExpired(selectedQuotation.validity) 
                      ? "#dc3545" 
                      : "inherit",
                    fontWeight: isValidityExpired(selectedQuotation.validity) 
                      ? 600 
                      : "normal",
                  }}
                >
                  {formatDate(selectedQuotation.validity)}
                </span>
                {isValidityExpired(selectedQuotation.validity) && (
                  <span
                    style={{
                      backgroundColor: "#dc3545",
                      color: "white",
                      padding: "2px 6px",
                      borderRadius: "8px",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      marginLeft: "8px",
                    }}
                  >
                    Expired
                  </span>
                )}
              </Col>
              <Col xs={12} md={6} xl={4}>
                <strong>Quotation Type :</strong>{" "}
                {formatLabel(selectedQuotation.quotation_creation_type)}
              </Col>
              <Col xs={12} md={6} xl={4}>
                <strong>Customer/Company Name :</strong>{" "}
                {selectedQuotation.customer_details.customer_name}
              </Col>
              <Col xs={12} md={6} xl={4}>
                <strong>Customer/Company Email :</strong>{" "}
                {selectedQuotation.customer_details.email}
              </Col>
              <Col xs={12} md={6} xl={4}>
                <strong>Phone No. :</strong>{" "}
                {selectedQuotation.customer_details.mobile_no}
              </Col>
            </Row>

            <hr />

            {/* Items Table */}
            <div className="">
              <Card className="p-0">
                <Card.Body>
                  <h5 className="mb-4">Items Details</h5>
                  <div className="">
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      className="align-middle"
                    >
                      <thead className="table-primary text-center">
                        <tr>
                          <th>Category</th>
                          <th>Item Name</th>
                          <th>Brand</th>
                          <th>Item SKU</th>
                          <th>Quantity</th>
                          <th>Selling Price (₹)</th>
                          <th>Discounts</th>
                          {/* <th>Tax Rate (%)</th> */}
                          <th>Total (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemsList.map((category) =>
                          category.items.map((item, index) => (
                            <tr key={item.item_id}>
                              {index === 0 && (
                                <td
                                  rowSpan={category.items.length}
                                  className="fw-semibold text-center"
                                >
                                  {category.category}
                                </td>
                              )}
                              <td>{item.item_name}</td>
                              <td>
                                {item.brand_name || "-"}
                              </td>
                              <td>{item.item_sku}</td>
                              <td className="text-center">{item.quantity}</td>
                              <td className="text-end">
                                {parseFloat(item.selling_price).toLocaleString(
                                  "en-IN"
                                )}
                              </td>
                              <td className="text-center">
                                {item.discount > 0
                                  ? `${item.discount}${
                                      item.discount_type === "%" ? "%" : "₹"
                                    }`
                                  : "-"}
                              </td>
                              {/* <td className="text-center">{item.tax_rate}</td> */}
                              <td className="text-end">
                                {item.item_amount.toLocaleString("en-IN")}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>

                    <Row className="mt-4">
                      {/* Images Section */}
                      <Col
                        xs={12}
                        lg={6}
                        className="order-1 order-lg-1 mb-3 mb-lg-0"
                      >
                        {(selectedQuotation.images.length > 0 ||
                          selectedQuotation.reference_images.length > 0) && (
                          <Card>
                            <Card.Body>
                              {selectedQuotation.images.length > 0 && (
                                <>
                                  <h5 className="mb-3">Upload Images</h5>
                                  <Row className="g-3">
                                    {selectedQuotation.images.map(
                                      (img: any) => (
                                        <Col
                                          xs={6}
                                          sm={4}
                                          md={3}
                                          key={img.img_id}
                                        >
                                          <div
                                            style={{
                                              position: "relative",
                                              display: "inline-block",
                                              width: "100%",
                                            }}
                                          >
                                            <img
                                              src={img.image}
                                              alt="Reference"
                                              className="img-fluid rounded"
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                              }}
                                            />

                                            <Button
                                              variant="contained"
                                              color="primary"
                                              onClick={() => setModalImage(img)}
                                              sx={{
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                minWidth: 0,
                                                padding: "4px",
                                                borderRadius: "50%",
                                                width: "20px",
                                                height: "20px",
                                                fontSize: "16px",
                                                lineHeight: 1,
                                                textTransform: "none",
                                              }}
                                            >
                                              i
                                            </Button>
                                          </div>
                                        </Col>
                                      )
                                    )}
                                  </Row>
                                </>
                              )}

                              {selectedQuotation.reference_images.length >
                                0 && (
                                <>
                                  <h5 className="mt-4 mb-3">
                                    Reference Images
                                  </h5>
                                  <Row className="g-3">
                                    {selectedQuotation.reference_images.map(
                                      (img: any) => (
                                        <Col
                                          xs={6}
                                          sm={4}
                                          md={3}
                                          key={img.ref_img_id}
                                        >
                                          <div
                                            style={{
                                              position: "relative",
                                              display: "inline-block",
                                              width: "100%",
                                            }}
                                          >
                                            <img
                                              src={img.image}
                                              alt="Reference"
                                              className="img-fluid rounded"
                                              style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "contain",
                                              }}
                                            />

                                            <Button
                                              variant="contained"
                                              color="primary"
                                              onClick={() => setModalImage(img)}
                                              sx={{
                                                position: "absolute",
                                                top: 8,
                                                right: 8,
                                                minWidth: 0,
                                                padding: "4px",
                                                borderRadius: "50%",
                                                width: "20px",
                                                height: "20px",
                                                fontSize: "16px",
                                                lineHeight: 1,
                                                textTransform: "none",
                                              }}
                                            >
                                              i
                                            </Button>
                                          </div>
                                        </Col>
                                      )
                                    )}
                                  </Row>
                                </>
                              )}
                            </Card.Body>
                          </Card>
                        )}

                        {selectedQuotation.notes && (
                          <div className="mt-4">
                            <h5 className="mb-2">Notes</h5>
                            <p className="mb-0">{selectedQuotation.notes}</p>
                          </div>
                        )}
                      </Col>

                      {/* Totals Section */}
                      <Col xs={12} lg={6} className="order-2 order-lg-2">
                        <Card>
                          <Card.Body>
                            <h5 className="mb-3">Totals</h5>
                            <Table bordered className="mb-0">
                              <tbody className="table-light fw-bold">
                                {/* <tr>
                                  <td className="text-end">Total Item Price</td>
                                  <td className="text-end">
                                    {itemsList
                                      .reduce((total, category) => {
                                        return (
                                          total +
                                          category.items.reduce(
                                            (categoryTotal, item) => {
                                              return (
                                                categoryTotal +
                                                parseFloat(item.selling_price) *
                                                  item.quantity
                                              );
                                            },
                                            0
                                          )
                                        );
                                      }, 0)
                                      .toLocaleString("en-IN")}
                                  </td>
                                </tr> */}

                                <tr>
                                  <td className="text-end">Subtotal</td>
                                  <td className="text-end">
                                    {parseFloat(
                                      selectedQuotation.subtotal
                                    ).toLocaleString("en-IN")}
                                  </td>
                                </tr>

                                {/* Assembly Charges */}
                                {selectedQuotation.assembly_charges &&
                                  selectedQuotation.assembly_charges.total !==
                                    undefined &&
                                  selectedQuotation.assembly_charges.total !==
                                    null &&
                                  selectedQuotation.assembly_charges.total >
                                    0 && (
                                    <tr>
                                      <td className="text-end">
                                        Assembly Charges
                                      </td>
                                      <td className="text-end">
                                        {parseFloat(
                                          selectedQuotation.assembly_charges
                                            .price || "0"
                                        ).toLocaleString("en-IN")}
                                      </td>
                                    </tr>
                                  )}
                                {/* Other Charges */}
                                {selectedQuotation.other_charges &&
                                  selectedQuotation.other_charges.length > 0 &&
                                  selectedQuotation.other_charges.map(
                                    (charge, index) => (
                                      <tr key={index}>
                                        <td className="text-end">
                                          {charge.name}
                                        </td>
                                        <td className="text-end">
                                          {charge.price?.toLocaleString(
                                            "en-IN"
                                          ) || "0"}
                                        </td>
                                      </tr>
                                    )
                                  )}

                                {/* <tr>
                                  <td className="text-end">Subtotal</td>
                                  <td className="text-end">
                                    {parseFloat(
                                      selectedQuotation.subtotal
                                    ).toLocaleString("en-IN")}
                                  </td>
                                </tr> */}

                                <tr>
                                  <td className="text-end">Total Discount</td>
                                  <td className="text-end">
                                    {parseFloat(
                                      selectedQuotation.total_item_discount
                                    ).toLocaleString("en-IN")}
                                  </td>
                                </tr>

                                {/* Space */}
                                {/* <tr
                                  style={{
                                    backgroundColor: "#f8f9fa",
                                    height: "8px",
                                  }}
                                >
                                  <td
                                    style={{ border: "none", padding: "4px" }}
                                  ></td>
                                  <td
                                    style={{ border: "none", padding: "4px" }}
                                  ></td>
                                </tr> */}

                                <tr>
                                  <td className="text-end">
                                    Net Taxable Value
                                  </td>
                                  <td className="text-end">
                                    {parseFloat(
                                      selectedQuotation.net_taxable_value
                                    ).toLocaleString("en-IN")}
                                  </td>
                                </tr>
                                <tr>
                                  <td className="text-end">
                                    Total GST Amount (18%)
                                  </td>
                                  <td className="text-end">
                                    {parseFloat(
                                      selectedQuotation.total_gst_amount
                                    ).toLocaleString("en-IN")}
                                  </td>
                                </tr>

                                <tr className="table-secondary">
                                  <td
                                    className="text-end"
                                    style={{ fontSize: "1rem" }}
                                  >
                                    Total Amount
                                  </td>
                                  <td
                                    className="text-end"
                                    style={{ fontSize: "1rem" }}
                                  >
                                    {parseFloat(
                                      selectedQuotation.quotation_total_amount
                                    ).toLocaleString("en-IN")}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </Card.Body>

                <Modal
                  show={!!modalImage}
                  onHide={() => setModalImage(null)}
                  size="xl"
                  centered
                >
                  <Modal.Header closeButton>
                    <Modal.Title>Image Preview</Modal.Title>
                  </Modal.Header>
                  <Modal.Body className="text-center">
                    {modalImage && (
                      <>
                        <img
                          src={modalImage.image}
                          alt="Full Preview"
                          className="img-fluid"
                          style={{ maxHeight: "90vh", objectFit: "contain" }}
                        />
                        {modalImage && modalImage.url && (
                          <div className="mt-3">
                            <h6>URL: {modalImage.url}</h6>
                          </div>
                        )}
                      </>
                    )}
                  </Modal.Body>
                </Modal>
              </Card>
            </div>

            <div className="d-flex justify-content-center gap-3 my-4">
              <Button
                variant="outlined"
                className="btn btn-secondary"
                onClick={handleBack}
                style={{ fontFamily: "Poppins, sans-serif", textTransform: "none" }}
              >
                Back
              </Button>
              {/* <Button variant="contained" className="btn btn-primary">
                Download PDF
              </Button> */}
            </div>
          </Card.Body>
        )}
      </Card>
    </Container>
  );
};

export default ViewTrashQuotationDetails;
